import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldQuestion, Mail, ArrowRight, Loader2, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthDialog from "@/components/AuthDialog";
import { API, useAuth } from "@/App";

export default function ClaimPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // { status, message, business_id }
  const [pin, setPin] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const pinInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/claims/verify/${token}`);
        if (!cancelled) setInvite(res.data);
      } catch (e) {
        if (!cancelled)
          setError(e.response?.data?.detail || "This claim link is invalid.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleClaim = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (invite?.pin_required) {
      const cleaned = pin.replace(/\D/g, "");
      if (cleaned.length !== 6) {
        toast.error("Enter the 6-digit PIN from your invite email.");
        pinInputRef.current?.focus();
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/claims/${token}/accept`,
        { pin: pin.replace(/\D/g, "") || undefined },
        { withCredentials: true }
      );
      setOutcome(res.data);
      if (res.data.status === "approved") {
        toast.success("Claim approved — you now own this business.");
      } else {
        toast.success("Claim submitted for admin review.");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not submit your claim.";
      toast.error(msg);
      // If the PIN was wrong, refresh invite to reflect possible lockout.
      if (e.response?.status === 400 || e.response?.status === 429) {
        try {
          const verify = await axios.get(`${API}/claims/verify/${token}`);
          setInvite(verify.data);
        } catch { /* ignore */ }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-tbc-cream/30" data-testid="claim-page">
      <Navbar />
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Banner */}
          <div className="bg-tbc-navy text-white px-6 py-5 flex items-center gap-3">
            <ShieldQuestion className="w-5 h-5 text-tbc-gold" />
            <div className="text-xs uppercase tracking-[0.25em]">
              Business ownership claim
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {loading && (
              <div className="text-center py-10 text-gray-500">
                <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                Verifying your link…
              </div>
            )}

            {!loading && error && (
              <div className="text-center py-6" data-testid="claim-error">
                <AlertTriangle className="w-10 h-10 mx-auto text-red-400 mb-3" />
                <h1 className="font-heading text-2xl text-tbc-navy mb-2">
                  We couldn&apos;t verify this link
                </h1>
                <p className="text-gray-600 mb-6">{error}</p>
                <Link to="/directory">
                  <Button variant="outline">Back to directory</Button>
                </Link>
              </div>
            )}

            {!loading && invite && !outcome && (
              <div data-testid="claim-form">
                {invite.business_image && (
                  <img
                    src={invite.business_image}
                    alt={invite.business_name}
                    className="w-full h-48 object-cover rounded-xl mb-4"
                  />
                )}
                <h1 className="font-heading text-3xl text-tbc-navy">
                  Claim {invite.business_name}
                </h1>
                {invite.business_category && (
                  <p className="text-xs uppercase tracking-widest text-tbc-gold mt-1">
                    {invite.business_category}
                  </p>
                )}
                <p className="text-gray-600 mt-4">
                  Sign in with the Google account you use for this business — if it matches
                  the contact email we have on file, you&apos;ll be set up instantly. Otherwise,
                  your claim goes to admin review.
                </p>

                {invite.expected_email_masked && (
                  <div className="mt-5 flex items-center gap-2 text-sm text-gray-600 bg-tbc-cream/60 rounded-lg p-3">
                    <Mail className="w-4 h-4 text-tbc-navy" />
                    Expected email on file:&nbsp;
                    <span className="font-mono text-tbc-navy">
                      {invite.expected_email_masked}
                    </span>
                  </div>
                )}

                {user && (
                  <div className="mt-3 text-sm text-gray-600">
                    Signed in as <span className="font-medium">{user.email}</span>
                  </div>
                )}

                {/* One-time PIN input (only when the invite was issued with a PIN) */}
                {invite.pin_required && (
                  <div className="mt-6 bg-tbc-cream/40 border border-tbc-gold/40 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-4 h-4 text-tbc-navy" />
                      <span className="text-sm font-medium text-tbc-navy">
                        Enter the 6-digit PIN from your invite email
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      We sent a PIN along with your claim link to protect against forwarded
                      or leaked URLs. You have {5} attempts.
                    </p>
                    {invite.pin_locked ? (
                      <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>
                          Too many wrong PIN attempts. Contact the team to have a new invite
                          issued.
                        </span>
                      </div>
                    ) : (
                      <Input
                        ref={pinInputRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d*"
                        maxLength={6}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="123456"
                        className="text-center font-mono text-2xl tracking-[0.4em] h-14 bg-white"
                        data-testid="claim-pin-input"
                      />
                    )}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    onClick={handleClaim}
                    disabled={submitting || invite.pin_locked}
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6"
                    data-testid="claim-submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    {user
                      ? "Claim this business"
                      : "Sign in to claim"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Link to={`/business/${invite.business_id}`}>
                    <Button variant="outline" className="rounded-full">
                      View business page
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            {!loading && outcome && (
              <div className="text-center py-6" data-testid="claim-outcome">
                {outcome.status === "approved" ? (
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                ) : (
                  <ShieldQuestion className="w-12 h-12 mx-auto text-yellow-500 mb-3" />
                )}
                <h1 className="font-heading text-2xl text-tbc-navy mb-2">
                  {outcome.status === "approved" ? "You're in" : "Submitted for review"}
                </h1>
                <p className="text-gray-600 mb-6">{outcome.message}</p>
                {outcome.status === "approved" ? (
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white rounded-full px-6"
                    data-testid="claim-go-dashboard"
                  >
                    Open my dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Link to={`/business/${outcome.business_id}`}>
                    <Button variant="outline" className="rounded-full">
                      View business page
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Footer />
    </div>
  );
}
